// =============================================================================
// WFC - GA4 measurement layer  (marketing site)
// =============================================================================
// Emits the marketing-site events from the "Events and CTAs to Track" handoff
// (sections C-F) as structured pushes to window.dataLayer. GTM (which is live
// and working on the marketing site) forwards each to GA4, property
// G-7YPR73F83N. The checkout / purchase funnel (section B) lives in the SEPARATE
// checkout app and is NOT handled here.
//
// CONTRACT - declared in the markup, read here (one place, no per-page JS):
//   data-ga-event   the GA4 event name (snake_case)
//   data-ga-params  static params as a JSON object - used on click elements
//   data-ga-compute names a field-reader (below) for form submits
//   data-ga-p-KEY   one dynamic param value (safe to build from ERB output)
//
// RULES (from the handoff - do not break):
//   - Never send personal or open-text field values. The concierge message box
//     and any contact-detail fields are NEVER read or pushed. See the field
//     readers below - they emit only coarse, non-identifying params.
//   - Context goes in PARAMS, not the event name (one join_cta_click +
//     cta_location, never five separate events).
//   - Forms fire on SUBMIT (intent to send). For the Key events
//     (partner_enquiry_submitted, request_submitted) the production-hardened path
//     is a SERVER-CONFIRMED push on the success render - see docs/ANALYTICS.md.
// =============================================================================

// Bucket a raw guest count into a coarse, non-identifying range (guests_bucket).
function guestsBucket(raw) {
  var n = parseInt(raw, 10);
  if (!n || n < 1) return "unknown";
  if (n === 1) return "1";
  if (n === 2) return "2";
  if (n <= 4) return "3-4";
  if (n <= 8) return "5-8";
  return "9+";
}

function field(form, name) {
  var el = form.querySelector('[name="' + name + '"]');
  return el ? String(el.value || "").trim() : "";
}

// Compute-param registry - form submits whose params come from field values.
// Each returns ONLY coarse, non-identifying params. Keyed by data-ga-compute.
var COMPUTE = {
  // For Business enquiry. partner_type only (client | employee | both).
  partnerEnquiry: function (form) {
    return { partner_type: field(form, "partner_type") || "unknown" };
  },
  // Concierge request (destination pages). city / guests_bucket / has_date.
  // The open-text "request" field is DELIBERATELY never read or sent.
  conciergeRequest: function (form) {
    return {
      city: form.getAttribute("data-ga-city") || "",
      guests_bucket: guestsBucket(field(form, "group_size")),
      has_date: field(form, "date") !== ""
    };
  },
  // Consumer contact form. form_location identifies the placement.
  contactForm: function (form) {
    return { form_location: form.getAttribute("data-ga-form-location") || "about" };
  },
  // Newsletter opt-in.
  newsletter: function (form) {
    return { source: form.getAttribute("data-ga-source") || "stories_footer" };
  }
};

function readParams(el) {
  var params = {};
  // Static params as one JSON blob - used where values are literals (join CTAs).
  var json = el.getAttribute("data-ga-params");
  if (json) {
    try { Object.assign(params, JSON.parse(json)); }
    catch (e) { if (window.__wfcAnalyticsDebug) console.warn("[wfc-ga] bad data-ga-params", json); }
  }
  // Per-attribute params: data-ga-p-<key>="value". Safer for dynamic ERB values
  // (venue/city names) - HTML-attribute escaping handles quotes; no JSON to break.
  if (el.attributes) {
    for (var i = 0; i < el.attributes.length; i++) {
      var a = el.attributes[i];
      if (a.name.indexOf("data-ga-p-") === 0) {
        params[a.name.slice(10).replace(/-/g, "_")] = a.value;
      }
    }
  }
  return params;
}

function pushEvent(name, params) {
  if (!name) return;
  window.dataLayer = window.dataLayer || [];
  var payload = Object.assign({ event: name }, params || {});
  window.dataLayer.push(payload);
  if (window.__wfcAnalyticsDebug) console.log("[wfc-ga]", payload);
}

function onClick(e) {
  var el = e.target.closest && e.target.closest("[data-ga-event]");
  if (!el || el.tagName === "FORM") return;   // forms fire on submit, not click
  pushEvent(el.getAttribute("data-ga-event"), readParams(el));
}

function onSubmit(e) {
  var form = e.target;
  if (!form || !form.matches || !form.matches("form[data-ga-event]")) return;
  var params = readParams(form);
  var compute = form.getAttribute("data-ga-compute");
  if (compute && COMPUTE[compute]) {
    try { Object.assign(params, COMPUTE[compute](form) || {}); } catch (err) {}
  }
  // Sync push: GTM overrides dataLayer.push and processes it synchronously, and
  // GA4 sends via sendBeacon which survives the ensuing navigation.
  pushEvent(form.getAttribute("data-ga-event"), params);
}

// Listeners are attached ONCE at the document level (they survive Turbolinks
// navigations), so initAnalytics is idempotent across turbolinks:load.
export function initAnalytics(/* root */) {
  if (window.__wfcAnalyticsBound) return;
  window.__wfcAnalyticsBound = true;
  window.dataLayer = window.dataLayer || [];
  document.addEventListener("click", onClick, { passive: true });
  document.addEventListener("submit", onSubmit, true);
}
